import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Download, FileUp, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { errMsg } from "@/lib/api";

async function downloadCsv() {
  const res = await api.get("/groups/export", { responseType: "blob" });
  const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || "senarai-group.csv";
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export default function GroupImportExport({ onImported }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [replace, setReplace] = useState(true);

  const exportCsv = async () => {
    setBusy("export");
    try { await downloadCsv(); toast.success("Senarai group dimuat turun"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy("import");
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/groups/import", form, { params: { replace } });
      setResult(data);
      toast.success(data.replaced ? `${data.removed} group lama dipadam, ${data.added} group baharu dimasukkan` : `${data.added} group ditambah`);
      onImported();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); ref.current.value = ""; }
  };

  return (
    <>
      <Button variant="secondary" onClick={exportCsv} disabled={!!busy} data-testid="groups-export-btn">
        {busy === "export" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />} Export
      </Button>
      <Button variant="secondary" onClick={() => { setResult(null); setOpen(true); }} data-testid="groups-import-btn">
        <Upload className="mr-2 h-4 w-4" /> Import
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-border bg-card" data-testid="groups-import-dialog">
          <DialogHeader>
            <DialogTitle>Import senarai group</DialogTitle>
            <DialogDescription>Muat naik fail CSV (boleh disimpan dari Excel / Google Sheets).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>Lajur: <code className="text-slate-200">nama, pautan, kategori, nota, perlu_kelulusan</code>. Hanya <b className="text-slate-200">pautan</b> yang wajib — fail yang hanya ada senarai link pun boleh.</p>
            <div className="flex items-center justify-between gap-3 rounded-lg border border-border p-3">
              <Label htmlFor="import-replace" className="cursor-pointer text-slate-200">Ganti semua group lama</Label>
              <Switch id="import-replace" checked={replace} onCheckedChange={setReplace} data-testid="groups-import-replace-switch" />
            </div>
            {replace ? (
              <p className="flex gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-amber-200" data-testid="groups-import-replace-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                SEMUA group sedia ada akan dipadam dan diganti dengan senarai dalam fail. Post terjadual tidak terjejas.
              </p>
            ) : (
              <p>Group dengan pautan yang sudah wujud akan dilangkau secara automatik.</p>
            )}
            <button onClick={exportCsv} className="text-primary hover:underline" data-testid="groups-import-template-btn">Muat turun fail contoh (export semasa)</button>
          </div>
          <input ref={ref} type="file" accept=".csv,text/csv,text/plain" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="groups-import-file-input" />
          <Button onClick={() => ref.current.click()} disabled={!!busy} className="w-full" data-testid="groups-import-choose-btn">
            {busy === "import" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />} Pilih fail CSV
          </Button>
          {result && (
            <div className={`grid gap-2 text-center ${result.replaced ? "grid-cols-4" : "grid-cols-3"}`} data-testid="groups-import-result">
              {result.replaced && <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3"><p className="font-mono text-2xl text-amber-300">{result.removed}</p><p className="text-xs text-muted-foreground">Dipadam</p></div>}
              <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3"><p className="font-mono text-2xl text-emerald-300">{result.added}</p><p className="text-xs text-muted-foreground">Ditambah</p></div>
              <div className="rounded-lg border border-border p-3"><p className="font-mono text-2xl">{result.skipped}</p><p className="text-xs text-muted-foreground">{result.replaced ? "Pendua" : "Sudah wujud"}</p></div>
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-3"><p className="font-mono text-2xl text-red-300">{result.invalid}</p><p className="text-xs text-muted-foreground">Pautan tidak sah</p></div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
